import React, { useState } from 'react';
import { Modal, View, Text, StyleSheet, TouchableOpacity, SafeAreaView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { PlannedMeal, markMealAsCooked } from '../../redux/mealPlanSlice';
import { consumeItemsForCooking } from '../../redux/inventorySlice';
import { dismissFallbackMeal } from '../../redux/userPreferencesSlice';
import { useTranslation } from 'react-i18next';

interface CookedWithoutModeSheetProps {
  meal: PlannedMeal;
  userId?: string;
  onDismiss: () => void;
}

const CookedWithoutModeSheet: React.FC<CookedWithoutModeSheetProps> = ({ meal, userId, onDismiss }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const recipeName = meal.recipe?.title ?? t('cookingPlan.thisMeal');

  const handleMarkCookedWithUpdate = async () => {
    setIsLoading(true);
    try {
      await dispatch(
        consumeItemsForCooking({
          extendedIngredients: meal.recipe?.extendedIngredients ?? [],
          servings: meal.servings,
          plannedServings: meal.recipe?.servings ?? meal.servings,
          userId,
        })
      ).unwrap();
      dispatch(markMealAsCooked({ mealId: meal.id }));
      dispatch(dismissFallbackMeal(meal.id));
      onDismiss();
    } catch (error) {
      // On error, still mark as cooked and dismiss
      dispatch(markMealAsCooked({ mealId: meal.id }));
      dispatch(dismissFallbackMeal(meal.id));
      onDismiss();
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkCookedWithoutUpdate = () => {
    dispatch(markMealAsCooked({ mealId: meal.id }));
    dispatch(dismissFallbackMeal(meal.id));
    onDismiss();
  };

  const handleNotCooked = () => {
    dispatch(dismissFallbackMeal(meal.id));
    onDismiss();
  };

  return (
    <Modal transparent={true} animationType="slide">
      <View style={styles.backdrop} />
      <View style={styles.centeredView}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.sheetCard}>
            {/* Drag handle */}
            <View style={styles.dragHandle} />

            {/* Title */}
            <Text style={styles.title}>{t('cookingPlan.cookedQuestion', { recipe: recipeName })}</Text>

            {/* Subtitle */}
            <Text style={styles.subtitle}>{t('cookingPlan.updatePantryHint')}</Text>

            {/* Button 1: Yes, update pantry */}
            <TouchableOpacity
              disabled={isLoading}
              onPress={handleMarkCookedWithUpdate}
              activeOpacity={0.8}
            >
              <LinearGradient
                colors={['#864e2c', '#a36642']}
                style={styles.primaryButton}
              >
                <Text style={styles.primaryButtonText}>
                  {isLoading ? t('cookingPlan.updating') : t('cookingPlan.yesUpdatePantry')}
                </Text>
              </LinearGradient>
            </TouchableOpacity>

            {/* Button 2: Yes, without update */}
            <TouchableOpacity
              onPress={handleMarkCookedWithoutUpdate}
              activeOpacity={0.7}
              style={styles.secondaryButton}
            >
              <Text style={styles.secondaryButtonText}>{t('cookingPlan.yesWithoutUpdate')}</Text>
            </TouchableOpacity>

            {/* Button 3: Not cooked */}
            <TouchableOpacity
              onPress={handleNotCooked}
              activeOpacity={0.7}
              style={styles.tertiaryButton}
            >
              <Text style={styles.tertiaryButtonText}>{t('cookingPlan.noNotCooked')}</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  centeredView: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  safeArea: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 32,
  },
  dragHandle: {
    width: 32,
    height: 4,
    backgroundColor: '#d7c2b8',
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    fontStyle: 'italic',
    color: '#1b1c1a',
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: '#52443c',
    marginBottom: 24,
  },
  primaryButton: {
    borderRadius: 24,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: '#864e2c',
    borderRadius: 24,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  secondaryButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: '#864e2c',
    fontWeight: '600',
  },
  tertiaryButton: {
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  tertiaryButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: '#52443c',
    fontWeight: '600',
  },
});

export default CookedWithoutModeSheet;
